import { sendMessageToParent } from "../../useParentCommunication";
import {
	TypeFooter_groupFields,
	TypeFooterBrandNavigation
} from "../contentTypes";
import * as Contentful from "contentful";
import { Remarkable } from "remarkable";
import { getLinkHref } from "../../../helpers";
import { Document } from "@contentful/rich-text-types";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { getLegalDisclaimerNodeOption } from "../../../../components/LegalDisclaimer";

const convertTarget = (target: string) => {
	return target === "External" ? "_blank" : "_top";
};

const readImageLink = (
	node: Contentful.Entry<TypeFooterBrandNavigation>,
	vendor: string | undefined
) => {
	const {
		fields: {
			title,
			url,
			target,
			media: {
				fields: {
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					title: mediaTitle,
					file: {
						url: mediaUrl,
						details: { image: { width, height } = {} }
					}
				}
			}
		}
	} = node;

	return {
		altText: title,
		imageUrl: mediaUrl,
		imageWidth: width,
		imageHeight: height,
		href: getLinkHref(url, vendor),
		target: convertTarget(target)
	};
};

export const readLogo = (
	node?: Contentful.Entry<TypeFooterBrandNavigation>,
	vendor?: string | undefined
) => {
	if (node) {
		return readImageLink(node, vendor);
	}
	return {
		altText: "",
		imageUrl: "",
		imageWidth: 0,
		imageHeight: 0,
		target: "_top"
	};
};

export const readLegalDisclaimer = (node?: string) => {
	const md = new Remarkable();
	md.set({ linkTarget: "_blank" });
	node ??= "";
	return md.render(node);
};

export const readSupportLinks = (
	nodes: Contentful.Entry<TypeFooter_groupFields>[],
	localeCase: (text?: string) => string | undefined,
	vendor: string | undefined
) => {
	if (nodes) {
		try {
			return nodes[0].fields.items.map((it) => {
				return {
					copy: localeCase(it.fields.linkBodyText),
					url: getLinkHref(it.fields.url, vendor),
					altText: it.fields.linkAltText,
					target: "_blank"
				};
			});
		} catch (ex) {
			// eslint-disable-next-line no-console
			console.error("unprocessable suppport link", nodes, ex);
		}
	}
	return [];
};

export const readMenuItems = (
	node: Contentful.Entry<TypeFooter_groupFields>[],
	localeCase: (text?: string) => string | undefined,
	vendor: string | undefined
) => {
	const menuItems: {
		title: string;
		links: object[];
	}[] = [];
	node ??= [];
	node.map((nodeItem) => {
		try {
			const menuLinks: object[] = [];
			nodeItem.fields.items.map((linkNode: any) => {
				try {
					const target =
						linkNode.fields.target === "Internal" ? "_top" : "_blank";

					const menuLink = {
						copy: localeCase(linkNode.fields.linkBodyText),
						url: getLinkHref(linkNode.fields.url, vendor),
						target: target
					};
					const vendorLinks = ["footer-my-account", "footer-re-orders"];
					if (
						vendor !== "ricoh" &&
						vendorLinks.includes(linkNode.fields.title)
					) {
						menuLinks.push({ ...menuLink, id: linkNode.fields.title });
						return;
					}
					menuLinks.push(menuLink);
				} catch (ex) {
					// eslint-disable-next-line no-console
					console.error("unprocessable footer navigation item", linkNode, ex);
				}
			});

			menuItems.push({
				title: localeCase(nodeItem.fields.titleText) || "",
				links: menuLinks
			});
		} catch (ex) {
			// eslint-disable-next-line no-console
			console.error("unprocessable footer navigation item", nodeItem, ex);
		}
	});
	return menuItems;
};

export const readSocials = (
	node: Contentful.Entry<TypeFooter_groupFields>[],
	localeCase: (text?: string) => string | undefined,
	vendor: string | undefined
) => {
	const menuItems: {
		title: string;
		links: object[];
	}[] = [];
	node ??= [];
	node.map((nodeItem) => {
		try {
			const menuLinks: object[] = [];
			nodeItem.fields.items.map((linkNode: any) => {
				try {
					const target =
						linkNode.fields.target === "Internal" ? "_top" : "_blank";
					menuLinks.push({
						altText: linkNode.fields.linkAltText,
						icon: linkNode.fields.icon,
						href: getLinkHref(linkNode.fields.url, vendor),
						mediaSrc: linkNode.fields.media?.fields?.file?.url,
						target: target
					});
				} catch (ex) {
					// eslint-disable-next-line no-console
					console.error("unprocessable footer navigation link", linkNode, ex);
				}
			});

			menuItems.push({
				title: localeCase(nodeItem.fields.titleText) || "",
				links: menuLinks
			});
		} catch (ex) {
			// eslint-disable-next-line no-console
			console.error("unprocessable footer social item", nodeItem, ex);
		}
	});
	const menuRoot = menuItems.length >= 1 ? menuItems[0] : { links: [] };
	return menuRoot;
};

export const readLanguage = (
	nodes: Contentful.Entry<TypeFooter_groupFields>[],
	localeCase: (text?: string) => string | undefined,
	vendor: string | undefined
) => {
	const updateLocale = (newParam: string) => {
		//const url = new URL(window.location.href)
		//url.searchParams.set('lng', newParam)
		//location.href = url.toString()
		sendMessageToParent({ locale: newParam });
	};
	let items = nodes && nodes.length >= 1 ? nodes[0]?.fields?.items : [];
	items ??= [];
	const langs = items.map((nodeItem) => {
		try {
			return {
				url: getLinkHref(nodeItem.fields.url, vendor),
				language: localeCase(nodeItem.fields.linkBodyText),
				onClick: () => updateLocale(nodeItem.fields.url),
				name: localeCase(nodeItem.fields.linkBodyText)
			};
		} catch (ex) {
			// eslint-disable-next-line no-console
			console.error("unprocessable footer language item", nodeItem, ex);
		}
	});

	return langs;
};

export const readLegalDisclaimerNode = (document: Document, vendor?: string) =>
	documentToReactComponents(document, getLegalDisclaimerNodeOption(vendor));
